/**
 * BrandedPlaceholder — gold-on-walnut placeholder used until real
 * booth/venue/event photography is uploaded.
 *
 * The user explicitly rejected generic stock photos because the
 * booths are a major selling point and stock would reduce
 * credibility. Until real images land, every visual slot renders
 * this branded placeholder: a quiet walnut gradient, hairline gold
 * border, and the subject's name in editorial serif.
 *
 * When real images arrive, callers pass `imageUrl` and the
 * placeholder steps aside.
 */
import React from 'react';
import {
  SERIF, IVORY, IVORY_DIM, GOLD, GOLD_FAINT,
  WALNUT_ALT, WALNUT_DEEP,
} from './_tokens';

const BrandedPlaceholder = ({
  label,
  sublabel,
  imageUrl,
  aspectRatio = '16 / 10',
  testId,
}) => {
  if (imageUrl) {
    return (
      <div
        data-testid={testId}
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio,
          borderRadius: 8,
          border: `1px solid ${GOLD_FAINT}`,
          overflow: 'hidden',
        }}
      >
        {/*
          iter237 · Mobile-safety fix — was CSS background-image
          (bypassed native lazy-loading, decoded every image in the
          list at once, crashed Chrome on Android during scroll on
          large portfolios). Native <img> gives us:
            • loading="lazy"   — only decodes as it nears viewport
            • decoding="async" — off the main thread
            • width/height=100% via aspectRatio wrapper — no CLS
        */}
        <img
          src={imageUrl}
          alt={label || ''}
          loading="lazy"
          decoding="async"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            display: 'block',
          }}
        />
      </div>
    );
  }
  return (
    <div
      data-testid={testId}
      data-branded-placeholder="true"
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio,
        background: `
          radial-gradient(ellipse at 50% 40%, rgba(212,170,82,0.10) 0%, rgba(212,170,82,0) 60%),
          linear-gradient(160deg, ${WALNUT_ALT} 0%, ${WALNUT_DEEP} 100%)
        `,
        border: `1px solid ${GOLD_FAINT}`,
        borderRadius: 8,
        overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 18px 60px rgba(0,0,0,0.45)',
      }}
    >
      {/* hairline corner marks — quietly editorial */}
      {[
        { top: 12, left: 12, borderTop: `1px solid ${GOLD_FAINT}`, borderLeft: `1px solid ${GOLD_FAINT}` },
        { top: 12, right: 12, borderTop: `1px solid ${GOLD_FAINT}`, borderRight: `1px solid ${GOLD_FAINT}` },
        { bottom: 12, left: 12, borderBottom: `1px solid ${GOLD_FAINT}`, borderLeft: `1px solid ${GOLD_FAINT}` },
        { bottom: 12, right: 12, borderBottom: `1px solid ${GOLD_FAINT}`, borderRight: `1px solid ${GOLD_FAINT}` },
      ].map((s, i) => (
        <span key={i} aria-hidden style={{ position: 'absolute', width: 18, height: 18, ...s }} />
      ))}

      {/* faint diamond at centre — matches editorial ✦ */}
      <div style={{ textAlign: 'center', padding: '0 20px' }}>
        <div style={{
          fontFamily: SERIF, fontSize: 36, color: GOLD,
          marginBottom: 10, lineHeight: 1, opacity: 0.78,
        }}>
          ✦
        </div>
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 'clamp(14px, 2vw, 18px)',
          color: IVORY,
          letterSpacing: '0.04em',
          marginBottom: sublabel ? 6 : 0,
        }}>
          {label}
        </div>
        {sublabel && (
          <div style={{
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 11.5,
            color: IVORY_DIM,
            letterSpacing: '0.16em', textTransform: 'uppercase',
          }}>
            {sublabel}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandedPlaceholder;
